import { useState } from 'react';
import {
  BookOpen,
  ChevronRight,
  X,
  MoreHorizontal,
  Trash2,
  XCircle,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';

interface SignalDetailHeaderProps {
  isActive: boolean;
  isCreator: boolean;
  isTogglingActive: boolean;
  onToggleActive: () => void;
  onRemove: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function SignalDetailHeader({
  isActive,
  isCreator,
  isTogglingActive,
  onToggleActive,
  onRemove,
  onDelete,
  onClose,
}: SignalDetailHeaderProps) {
  const { t } = useTranslation('knowledge');
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex items-center justify-between border-b px-5 py-3">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
        <BookOpen className="h-3.5 w-3.5 text-muted-foreground/60" />
        <span>{t('detailBreadcrumb')}</span>
        <ChevronRight className="h-2.5 w-2.5 text-muted-foreground/60 rtl:rotate-180" />
        <span className="font-medium text-foreground">
          {t('detailBreadcrumbCurrent')}
        </span>
      </div>

      {/* Right: Menu + Close */}
      <div className="flex items-center gap-2">
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
              aria-label="More options"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-44 p-1">
            <button
              className="flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted"
              disabled={isTogglingActive}
              onClick={() => {
                setMenuOpen(false);
                onToggleActive();
              }}
            >
              {isActive ? (
                <ToggleLeft className="h-3.5 w-3.5" />
              ) : (
                <ToggleRight className="h-3.5 w-3.5" />
              )}
              {isActive ? t('deactivate') : t('activate')}
            </button>
            {!isCreator && (
              <button
                className="flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-amber-50 hover:text-amber-600"
                onClick={() => {
                  setMenuOpen(false);
                  onRemove();
                }}
              >
                <XCircle className="h-3.5 w-3.5" />
                {t('detailRemove')}
              </button>
            )}
            {isCreator && (
              <button
                className="flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onDelete();
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('detailDeleteSignal')}
              </button>
            )}
          </PopoverContent>
        </Popover>

        <button
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
